import React, { FunctionComponent, useCallback, useState } from 'react';
import { loadStripe } from "@stripe/stripe-js";
import { Page } from '../Page';
import { Box } from '@mui/material';
import { BaseProps } from '../types';
import { Panel } from '../common/Panel';
import { Button } from '../common/Button';
import { TextFieldItem, TextFieldItemProps } from '../common/FormField';
import { Navigate, useNavigate } from 'react-router-dom';
import ErrorAlert from '../common/ErrorAlert';
import { connect } from 'react-redux';
import Text from '../common/styled/Text';
import { useQuery } from '../common/useQuery';
import { cleanPhoneNumber, formatPhoneNumber, hasValue, validateEmail } from '../../common/utils';
import { register } from '../../actions/register';
import TermsAndConditionsModal from '../TermsConditionsPrivacy/TermsAndConditionsModal';
import { RegisterFields } from '../../actions/types/register';
import { RegisterResponse } from '../../actions/types/responses';
import { useAuth } from '../../common/hooks/useAuth';

const VALID_PLANS = ['portfolio_designer', 'advisor_workstation', 'enterprise']

const RegisterRow: FunctionComponent<BaseProps> = ({ children }) => {
    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            maxWidth: {
                xs: 'unset',
                md: '74rem'
            },
            flexDirection: {
                xs: 'column',
                md: 'row'
            }
        }}>
            {children}
        </Box>
    )
}

interface RegisterFieldProps extends TextFieldItemProps {
    required?: boolean
}

const RegisterField: FunctionComponent<RegisterFieldProps> = ({ title, required, sx = { }, ...props }) => {
    const fieldTitle = <Text type={'table_header'}>{title}{required ? ' *' : ''}</Text>
    
    return (
        <TextFieldItem
            sx={{ 
                width: {
                    xs: '100%',
                    md: '35rem'
                },
                ...sx,
            }}
            title={fieldTitle}
            {...props}
        />
    )
}

interface RegisterProps extends BaseProps {
    register: (payload: RegisterFields) => Promise<RegisterResponse>
}

const RegisterComponent: FunctionComponent<RegisterProps> = ({ register }) => {
    const { setAuthentication, authenticated } = useAuth()
    const query = useQuery()
    const navigate = useNavigate()

    const [errors, setErrors] = useState<{ title?: string, items?: string[]}>({ })
    const [firstName, setFirstName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [confirmEmail, setConfirmEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [confirmPassword, setConfirmPassword] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [showTermsDialog, setShowTermsDialog] = useState<boolean>(false)

    const hasErrors = (errors.items && errors.items.length > 0) ? true : false

    const activationCode = query.get('activationCode') || undefined
    const plan = query.get('plan') || ''
    if(!plan || !VALID_PLANS.includes(plan)) {
        navigate('/signup')
    }

    const stripeRedirect = useCallback(async(data) => {
        try {
            const stripe = await loadStripe(process.env.REACT_APP_STRIPE_API_KEY || '')
            await stripe?.redirectToCheckout({ sessionId: data.session.id })
        } catch(error: any) {
            setErrors({ title: 'Checkout Error', items: [error.message] })
        }
    }, [])

    const validateLocal = ({ phone, firstName, lastName, email, confirmEmail, password, confirmPassword, activationCode, plan }: RegisterFields) => {
        const cleanedPhone = cleanPhoneNumber(phone)
    
        const errors: string[] = []
    
        if(plan === 'enterprise' && !hasValue(activationCode)) {
            errors.push('Please enter your activation code')
        }
    
        if(!hasValue(firstName) || !hasValue(lastName)) {
            errors.push('Please enter your first and last name')
        }
    
        if(!validateEmail(email) || !validateEmail(confirmEmail)) {
            errors.push('Please enter a valid email address')
        } else if(email != confirmEmail) {
            errors.push('Your email and confirm email doesn\'t match')
        }
    
        if(!hasValue(password) || !hasValue(confirmPassword)) {
            errors.push('Please enter a password and confirm your password')
        } else if(password != confirmPassword) {
            errors.push('Your password and confirm password doesn\'t match')
        }
        
        if(cleanedPhone.length != 10) {
            errors.push('Please enter a 10 digit phone number')
        }
    
        if(errors.length > 0) {
            setErrors({ title: 'Validation Errors', items: errors })
    
            return false
        }

    
        return true
    }

    const onClearErrors = useCallback(() => {
        setErrors({ })
    }, [setErrors])

    const onFormatPhone = useCallback((value) => {
        setPhone(formatPhoneNumber(value))
    }, [phone])
    
    const onRegister = useCallback(async(acceptedTerms: boolean = false) => {
        onClearErrors()
       if(!validateLocal({ phone, firstName, lastName, email, confirmEmail, password, confirmPassword, activationCode, plan })) {
        return
       }

       if(activationCode && acceptedTerms !== true) {
        setShowTermsDialog(true)

        return
       }

       const { success, authorization, errors, ...restData } = await register({ phone, firstName, lastName, email, confirmEmail, password, confirmPassword, activationCode, plan })
       if(!success) {
        setErrors({ title: 'Registration Error', items: errors })
       } else if(authorization) {
        setAuthentication({ auth: authorization })
        navigate(restData?.path || '/dashboard')
       } else if(restData.sessionId) {
        stripeRedirect(restData)
       } else {
        setErrors({ title: 'Registration Error', items: ['An unknown error occurred trying to register, please try agian laster'] })
       }
        
    }, [phone, firstName, lastName, email, confirmEmail, password, confirmPassword, activationCode, plan]);

    const onTermsAccepted = useCallback(() => {
        setShowTermsDialog(false)
        onRegister(true)
    }, [onRegister])

    if(authenticated) {
        return <Navigate to={'/'} />
    }

    const planFormatted = plan?.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || '';

    return (
        <Page 
            contentsSx={{ 
                justifyContent: 'center',
            }}>
                <TermsAndConditionsModal visible={showTermsDialog} onTermsAccepted={onTermsAccepted} />
                <Box sx={{ marginTop: '5rem' }}>
                    <Panel
                        title={`Register - ${planFormatted}`}>
                        <ErrorAlert
                            title={errors?.title || 'Alert'}
                            sx={{ margin: '1rem' }}
                            visible={hasErrors}
                            wrapChildrenInArray={false}
                            onDismiss={onClearErrors}>
                                <Box sx={{ display: 'flex', flexDirection: 'column '}}>
                                    {errors.items && errors.items.map((error) => <Box key={error}>{error}</Box>)}
                                </Box>
                        </ErrorAlert>
                            <Box 
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '2rem',
                                }}>
                                <RegisterRow>
                                    <RegisterField
                                        sx={{ marginRight: { md: '2rem' } }}
                                        title={'First Name'}
                                        value={firstName}
                                        onValueChange={setFirstName}
                                        required
                                    />
                                    <RegisterField
                                        title={'Last Name'}
                                        value={lastName}
                                        onValueChange={setLastName}
                                        required
                                    />
                                </RegisterRow>
                                <RegisterRow>
                                    <RegisterField
                                        sx={{ marginRight: { md: '2rem' } }}
                                        type={'email'}
                                        title={'Email'}
                                        value={email}
                                        onValueChange={setEmail}
                                        required
                                    />
                                    <RegisterField
                                        type={'email'}
                                        title={'Confirm Email'}
                                        value={confirmEmail}
                                        onValueChange={setConfirmEmail}
                                        required
                                    />
                                </RegisterRow>
                                <RegisterRow>
                                    <RegisterField
                                        sx={{ marginRight: { md: '2rem' } }}
                                        type={'password'}
                                        title={'Password'}
                                        value={password}
                                        onValueChange={setPassword}
                                        required
                                    />
                                    <RegisterField
                                        type={'password'}
                                        title={'Password Confirmation'}
                                        value={confirmPassword}
                                        onValueChange={setConfirmPassword}
                                        required
                                    />
                                </RegisterRow>
                                <RegisterRow>
                                    <RegisterField
                                        sx={{ marginRight: { md: '2rem' } }}
                                        title={'Phone Number'}
                                        id={'phone'}
                                        name={'phone'}
                                        value={phone}
                                        placeholder={'888-888-8888 x1'}
                                        onValueChange={onFormatPhone}
                                        required
                                    />
                                    {activationCode &&
                                        <RegisterField
                                            title={'Activation Code'}
                                            value={activationCode}
                                            readOnly
                                        />
                                    }
                                </RegisterRow>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-around', marginTop: '2rem'}}>
                                    <Button 
                                        title={'Continue'} 
                                        onClick={onRegister}
                                    />
                                    <Button 
                                        title={'Back'} 
                                        variant='outline'
                                        href='/'
                                    />
                                </Box>
                            </Box>
                    </Panel>
                </Box>
        </Page>
    )
}

export const Register = connect(null, { register })(RegisterComponent)